import { Form, QuestionView, QuestionOption, generateGuid, QuestionType, FormStatus } from '@formrails/form-builder/data-access';
import { CreateApplicationTemplateDto, CreateFormElementDto } from '../dtos/Requests/createApplicationTemplateDto';
import { QuestionResponseOptionDto } from '../dtos/questionResponseOptionDto';
import { SectionDto } from '../dtos/sectionDto';

/**
 * Mapper utility for converting Form model to CreateApplicationTemplateDto
 * Handles the conversion from UI form model to API request DTO
 */
export class FormToRequestMapper {
  /**
   * Maps FormStatus enum to API status string
   * Workflow: Draft -> Published (-> Archived)
   */
  private static mapFormStatusToApiStatus(status: FormStatus): 'Draft' | 'Published' | 'Archived' {
    const statusMap: Record<FormStatus, 'Draft' | 'Published' | 'Archived'> = {
      [FormStatus.Archived]: 'Archived',
      [FormStatus.Draft]: 'Draft',
      [FormStatus.Published]: 'Published'
    };
    return statusMap[status] || 'Draft';
  }

  /**
   * Maps QuestionType enum to FormElementType
   * Note: API expects "Textbox" not "Input"
   */
  private static mapQuestionTypeToFormElementType(questionType: QuestionType): string {
    const typeMap: Record<QuestionType, string> = {
      [QuestionType.TextBox]: 'Textbox',
      [QuestionType.CheckboxList]: 'Checkbox',
      [QuestionType.DropdownList]: 'Dropdown',
      [QuestionType.DynamicDropdown]: 'DynamicDropdown',
      [QuestionType.Email]: 'Email',
      [QuestionType.Date]: 'Date',
      [QuestionType.Tel]: 'Tel',
      [QuestionType.Radio]: 'Radio',
      [QuestionType.TextArea]: 'TextArea',
      [QuestionType.Toggle]: 'Toggle',
      [QuestionType.Confirmation]: 'Confirmation',
      [QuestionType.HelperText]: 'HelperText',
      [QuestionType.Section]: 'Section',
      [QuestionType.Title]: 'Title'
    };
    return typeMap[questionType] || 'Textbox';
  }

  /**
   * Converts QuestionOption[] to QuestionResponseOptionDto[]
   */
  private static convertOptionsToDto(options: (string | QuestionOption)[] | undefined): QuestionResponseOptionDto[] | null {
    if (!options || options.length === 0) {
      return null;
    }

    return options.map((opt, index) => {
      if (typeof opt === 'string') {
        return {
          id: generateGuid(),
          order: index + 1,
          text: opt,
          isCustomOption: false
        };
      } else {
        return {
          id: generateGuid(),
          order: index + 1,
          text: opt.text,
          isCustomOption: opt.isCustomOption || false
        };
      }
    });
  }

  /**
   * Coerces every validator value to a string so the payload matches the
   * server contract `Dictionary<string, string>`. Legacy question data may
   * carry booleans/numbers here — the server rejects those with a JSON
   * deserialisation error.
   */
  private static normaliseValidators(
    validators: Record<string, unknown> | null | undefined
  ): Record<string, string> | null {
    if (!validators) return null;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(validators)) {
      if (value === null || value === undefined) continue;
      out[key] = typeof value === 'string' ? value : String(value);
    }
    return Object.keys(out).length > 0 ? out : null;
  }

  /**
   * Converts a QuestionView to CreateFormElementDto
   */
  private static questionViewToFormElementDto(question: QuestionView, order: number): CreateFormElementDto {
    const questionType = question.questionType;
    if (!questionType) {
      throw new Error(`Question type is required for question: ${question.id}`);
    }

    // Check if this element has dynamic options (dropdowns or checkboxes)
    // Either via hasDynamicOptions flag or DynamicDropdown type
    const hasDynamicOptions = question.hasDynamicOptions || questionType === QuestionType.DynamicDropdown;

    // For dropdowns with dynamic options, send as 'Dropdown' type to the API with optionGenerationRules
    const typeString = (hasDynamicOptions && questionType === QuestionType.DynamicDropdown)
      ? 'Dropdown'
      : this.mapQuestionTypeToFormElementType(questionType);
    const options = this.convertOptionsToDto(question.options);

    // Build the DTO object, only including sectionId if it's defined
    const dto: CreateFormElementDto = {
      formElementId: question.id,
      order: order,
      displayNumber: question.questionNumber?.toString() || null,
      type: typeString as any, // Type assertion needed due to FormElementType union
      labelText: question.labelText || '',
      helperText: question.helperText || null,
      tooltip: question.tooltip || null,
      placeholder: question.placeholder || null,
      additionalInfo: question.additionalInfo || '',
      isSearchable: question.isSearchable || false,
      options: options,
      validators: this.normaliseValidators(question.validators)
    };

    // Only add sectionId if it's defined (not undefined or null)
    if (question.sectionId !== undefined && question.sectionId !== null) {
      dto.sectionId = question.sectionId;
    }

    // Add optionGenerationRules for elements with dynamic options (dropdowns and checkboxes)
    if (hasDynamicOptions) {
      dto.optionGenerationRules = {
        SourceContainer: question.sourceContainer || undefined,
        TextProperty: question.textProperty || undefined,
        CustomOptionId: question.customOptionId || undefined,
        CustomOptionText: question.customOptionText || undefined
      };
      console.log('FormToRequestMapper: Adding optionGenerationRules for', typeString, 'with dynamic options:', dto.optionGenerationRules);
    }

    return dto;
  }

  /**
   * Find the section ID for a question at a given index
   * Looks backwards to find the most recent Section before this position
   */
  private static findSectionId(questions: QuestionView[], index: number): string | undefined {
    // Look backwards from the current position to find the most recent Section
    for (let i = index - 1; i >= 0; i--) {
      if (questions[i].questionType === QuestionType.Section) {
        return questions[i].id;
      }
    }
    return undefined;
  }

  /**
   * Recalculate section IDs for all questions based on their position relative to sections
   * This ensures Title and HelperText elements have their sectionId set correctly
   */
  private static recalculateSectionIds(questions: QuestionView[]): QuestionView[] {
    if (!questions || questions.length === 0) {
      console.warn('recalculateSectionIds: No questions provided');
      return [];
    }
    
    // Sort all questions by order to ensure correct order
    const sortedQuestions = [...questions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    const sectionsInQuestions = sortedQuestions.filter(q => q.questionType === QuestionType.Section);
    console.log(`Recalculating sectionIds: Found ${sectionsInQuestions.length} sections in ${sortedQuestions.length} total questions`);
    console.log('Sections:', sectionsInQuestions.map((s: QuestionView) => ({ id: s.id, title: s.labelText, order: s.order })));
    console.log('All questions before recalculation:', sortedQuestions.map((q: QuestionView) => ({
      id: q.id,
      type: q.questionType,
      order: q.order,
      currentSectionId: q.sectionId
    })));

    const recalculated = sortedQuestions.map((q, index) => {
      // Section elements don't have sectionId (they are sections themselves)
      if (q.questionType === QuestionType.Section) {
        console.log(`Question ${q.id} (Section) at index ${index}: sectionId=undefined`);
        return { ...q, sectionId: undefined };
      }
      
      // Title and HelperText elements get sectionId but no questionNumber
      if (q.questionType === QuestionType.Title || q.questionType === QuestionType.HelperText) {
        const sectionId = this.findSectionId(sortedQuestions, index);
        const foundSection = sectionId ? sortedQuestions.find(s => s.id === sectionId) : null;
        console.log(`Question ${q.id} (${q.questionType}) at index ${index}: found sectionId=${sectionId} (section: ${foundSection?.labelText || 'none'})`);
        return {
          ...q,
          sectionId: sectionId
        };
      }
      
      // Regular questions already have sectionId, but ensure it's correct
      const sectionId = this.findSectionId(sortedQuestions, index);
      const foundSection = sectionId ? sortedQuestions.find(s => s.id === sectionId) : null;
      console.log(`Question ${q.id} (${q.questionType}) at index ${index}: found sectionId=${sectionId} (section: ${foundSection?.labelText || 'none'})`);
      return {
        ...q,
        sectionId: sectionId
      };
    });

    console.log('After recalculation:', recalculated.map((q: QuestionView) => ({
      id: q.id,
      type: q.questionType,
      order: q.order,
      sectionId: q.sectionId
    })));

    return recalculated;
  }

  /**
   * Extracts sections from questions and creates SectionDto[]
   */
  private static extractSections(questions: QuestionView[]): SectionDto[] {
    const sectionMap = new Map<string, { title: string; order: number; restricted: boolean }>();
    
    // Find all section questions
    questions.forEach((question, index) => {
      if (question.questionType === QuestionType.Section) {
        const sectionId = question.id;
        if (!sectionMap.has(sectionId)) {
          sectionMap.set(sectionId, {
            title: question.labelText || 'Untitled Section',
            order: question.order || index,
            restricted: false // Default to false, can be enhanced later
          });
        }
      }
    });

    // Convert to SectionDto[]
    return Array.from(sectionMap.entries()).map(([id, data]) => ({
      id: id,
      title: data.title,
      order: data.order,
      restricted: data.restricted
    }));
  }

  /**
   * Converts a Form model to CreateApplicationTemplateDto
   * @param form The form to convert
   * @param version Optional version number (defaults to form.version if available, otherwise 0)
   * @param description Optional description (defaults to form.description if available, otherwise form.title)
   */
  static toCreateDto(form: Form, version?: number, description?: string): CreateApplicationTemplateDto {
    console.log('toCreateDto called with form.questions:', form.questions.map((q: QuestionView) => ({
      id: q.id,
      type: q.questionType,
      order: q.order,
      sectionId: q.sectionId
    })));
    
    // Ensure we have all questions including sections for proper recalculation
    // First, recalculate sectionIds for all questions to ensure they're correct
    // This is important for Title and HelperText elements which might not have sectionId set
    const questionsWithSectionIds = this.recalculateSectionIds(form.questions);
    
    // Filter out section questions from form elements (sections are handled separately)
    const formElementQuestions = questionsWithSectionIds.filter(
      q => q.questionType !== QuestionType.Section
    );
    
    console.log('After filtering sections, formElementQuestions:', formElementQuestions.map((q: QuestionView) => ({
      id: q.id,
      type: q.questionType,
      order: q.order,
      sectionId: q.sectionId
    })));

    // Sort questions by order
    const sortedQuestions = [...formElementQuestions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    // Convert questions to form element DTOs
    // Note: optionGenerationRules is included for DynamicDropdown questions
    const formElements: CreateFormElementDto[] = sortedQuestions.map((question, index) => {
      // Use order if available, otherwise use index
      const order = question.order ?? (index + 1);
      console.log(`Converting question to DTO: id=${question.id}, type=${question.questionType}, sectionId=${question.sectionId}, order=${order}`);
      return this.questionViewToFormElementDto(question, order);
    });

    // Extract sections (use the recalculated questions to ensure correct order)
    const sections = this.extractSections(questionsWithSectionIds);

    // Use form title as name
    const name = form.title || 'Untitled Form';
    
    // Use form.description if available, otherwise fall back to description parameter, then to name
    const finalDescription = form.description || description || name;
    
    // Always use the explicit version parameter if provided (it's the calculated/authoritative value)
    // The version parameter is the source of truth - use it directly
    // Note: version can be 0 (a valid number for new drafts), so we check for undefined/null specifically
    let finalVersion = 0;
    if (version !== undefined && version !== null) {
      // Version parameter is provided - use it (even if it's 0)
      finalVersion = version;
    } else if (form.version !== undefined && form.version !== null) {
      // Fall back to form.version if parameter not provided
      finalVersion = form.version;
    }
    // Otherwise default to 0
    
    console.log('Version mapping in toCreateDto:', {
      'form.version': form.version,
      'version parameter': version,
      'version parameter type': typeof version,
      'version is undefined': version === undefined,
      'version is null': version === null,
      'version === 0': version === 0,
      'finalVersion': finalVersion,
      'finalVersion type': typeof finalVersion
    });

    return {
      name: name,
      description: finalDescription,
      version: finalVersion,
      status: this.mapFormStatusToApiStatus(form.status),
      assessmentMatrixIds: form.assessmentMatrixIds ?? [],
      applicationTemplateFormElements: formElements,
      sections: sections
    };
  }
}

